import React, { useCallback, useRef, useState } from 'react';
import { Box, IconButton, Popover, Tooltip } from '@mui/material';
import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import ShortcutIcon from '@mui/icons-material/Shortcut';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import AddTaskIcon from '@mui/icons-material/AddTask';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/common/reducers/hooks/store.hook';
import LazyEmojiPicker, { EmojiClickData, Theme } from '@/common/components/lazy-emoji-picker/lazy-emoji-picker';
import { selectIsDarkMode } from '@common-reducers/UserSelectors';
import { useMessageActions } from './use-message-actions';
import MessageContextMenu, { ContextMenuAnchor } from './message-context-menu';

// WhatsApp's default quick-reaction row.
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

interface MessageHoverActionsProps {
    message: any;
    fromMe: boolean;
    /** Row hover state from the parent; the bar also stays visible while any of
     *  its own popovers are open. */
    hovered: boolean;
    /** Cursor position from a right-click on the bubble (row-level), if any. */
    contextMenuPosition: { top: number; left: number } | null;
    /** Clears the right-click position (menu closed). */
    onContextMenuClose: () => void;
}

const roundButtonSx = {
    width: 26,
    height: 26,
    padding: 0,
    borderRadius: '50%',
    backgroundColor: 'var(--wa-hover-action-bg, #fff)',
    color: 'var(--wa-hover-action-icon, rgba(0,0,0,0.6))',
    boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
    '&:hover': { backgroundColor: 'var(--wa-hover-action-hover-bg, #f0f0f0)' },
    '& svg': { fontSize: 18 },
} as const;

/**
 * Floating pill of round action buttons shown beside a message on hover — React,
 * Forward, Note, Task, Reminder, plus a chevron opening the full WhatsApp-parity
 * context menu (also opened by right-click via contextMenuPosition). Metrics
 * follow the extension's WA-accurate injected bar. All action behavior lives in
 * useMessageActions (shared with the context menu).
 */
const MessageHoverActions: React.FC<MessageHoverActionsProps> = ({
    message,
    fromMe,
    hovered,
    contextMenuPosition,
    onContextMenuClose,
}) => {
    const { t } = useTranslation();
    const isDarkMode = useAppSelector(selectIsDarkMode);
    const actions = useMessageActions(message);

    const [reactAnchor, setReactAnchor] = useState<HTMLElement | null>(null);
    const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const reactBtnRef = useRef<HTMLButtonElement | null>(null);

    const menuOpen = Boolean(menuAnchorEl || contextMenuPosition);
    const menuAnchor: ContextMenuAnchor | null = menuAnchorEl
        ? { anchorEl: menuAnchorEl }
        : contextMenuPosition
            ? { anchorPosition: contextMenuPosition }
            : null;

    const anyPopoverOpen = Boolean(reactAnchor || pickerAnchor || menuOpen);
    const visible = hovered || anyPopoverOpen;

    const closeMenu = useCallback(() => {
        setMenuAnchorEl(null);
        onContextMenuClose();
    }, [onContextMenuClose]);

    // Context-menu "React" → open the quick bar anchored at the react button
    // (always in the DOM, even while the bar is transparent).
    const openReactionBar = useCallback(() => {
        setReactAnchor(reactBtnRef.current);
    }, []);

    const applyReaction = useCallback(async (emoji: string) => {
        setReactAnchor(null);
        setPickerAnchor(null);
        await actions.applyReaction(emoji);
    }, [actions]);

    return (
        <Box
            className="teamInboxHoverActions"
            sx={{
                position: 'absolute',
                top: '4px',
                // Beside the bubble toward screen center: left of sent bubbles,
                // right of received. Physical sides mirror the bubble alignment.
                ...(fromMe
                    ? { right: '100%', marginRight: '6px' }
                    : { left: '100%', marginLeft: '6px' }),
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                padding: '3px',
                borderRadius: '16px',
                backgroundColor: 'transparent',
                opacity: visible ? 1 : 0,
                pointerEvents: visible ? 'auto' : 'none',
                transition: 'opacity 0.12s ease',
                zIndex: 3,
                // Don't let a wide bar block text selection when hidden.
                whiteSpace: 'nowrap',
            }}
        >
            <Tooltip title={t('teamInbox.react', { defaultValue: 'React' })} arrow>
                {/* span: a Tooltip needs an element that fires events when the button is disabled */}
                <span>
                    <IconButton
                        ref={reactBtnRef}
                        size="small"
                        sx={roundButtonSx}
                        disabled={!actions.canReact}
                        onClick={(e) => setReactAnchor(e.currentTarget)}
                    >
                        <AddReactionOutlinedIcon />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title={t('teamInbox.forward', { defaultValue: 'Forward' })} arrow>
                <span>
                    <IconButton size="small" sx={roundButtonSx} disabled={!actions.canForward} onClick={actions.forwardMessage}>
                        <ShortcutIcon />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title={t('teamInbox.createNote', { defaultValue: 'Create note' })} arrow>
                <IconButton size="small" sx={roundButtonSx} onClick={actions.createNote}>
                    <StickyNote2OutlinedIcon />
                </IconButton>
            </Tooltip>

            <Tooltip title={t('teamInbox.createTask', { defaultValue: 'Create task' })} arrow>
                <IconButton size="small" sx={roundButtonSx} onClick={actions.createTask}>
                    <AddTaskIcon />
                </IconButton>
            </Tooltip>

            <Tooltip title={t('teamInbox.createReminder', { defaultValue: 'Create reminder' })} arrow>
                <IconButton size="small" sx={roundButtonSx} onClick={actions.createReminder}>
                    <NotificationsNoneIcon />
                </IconButton>
            </Tooltip>

            <Tooltip title={t('teamInbox.moreActions', { defaultValue: 'More' })} arrow>
                <IconButton
                    size="small"
                    sx={roundButtonSx}
                    onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                >
                    <ExpandMoreIcon />
                </IconButton>
            </Tooltip>

            {/* Full context menu (chevron or right-click) */}
            <MessageContextMenu
                message={message}
                actions={actions}
                open={menuOpen}
                anchor={menuAnchor}
                onClose={closeMenu}
                onOpenReactionBar={openReactionBar}
            />

            {/* Quick-reaction bar */}
            <Popover
                open={Boolean(reactAnchor)}
                anchorEl={reactAnchor}
                onClose={() => setReactAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                slotProps={{ paper: { sx: { borderRadius: '24px', overflow: 'visible', mt: '-6px' } } }}
            >
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    px: '6px',
                    py: '4px',
                    backgroundColor: 'var(--wa-hover-action-bg, #fff)',
                    borderRadius: '24px',
                }}>
                    {QUICK_REACTIONS.map((emoji) => (
                        <IconButton
                            key={emoji}
                            onClick={() => applyReaction(emoji)}
                            sx={{
                                width: 34, height: 34, fontSize: '22px',
                                '&:hover': { transform: 'scale(1.2)', backgroundColor: 'transparent' },
                                transition: 'transform 0.1s ease',
                            }}
                        >
                            <span style={{ lineHeight: 1 }}>{emoji}</span>
                        </IconButton>
                    ))}
                    <IconButton
                        onClick={(e) => { setPickerAnchor(e.currentTarget); }}
                        sx={{ width: 34, height: 34, color: 'var(--wa-hover-action-icon, rgba(0,0,0,0.6))' }}
                    >
                        <AddIcon />
                    </IconButton>
                </Box>
            </Popover>

            {/* Full emoji picker */}
            <Popover
                open={Boolean(pickerAnchor)}
                anchorEl={pickerAnchor}
                onClose={() => setPickerAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <LazyEmojiPicker
                    onEmojiClick={(d: EmojiClickData) => applyReaction(d.emoji)}
                    theme={isDarkMode ? Theme.DARK : Theme.LIGHT}
                />
            </Popover>
        </Box>
    );
};

export default React.memo(MessageHoverActions);
